import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import { FileUpload } from '@/components/kit/upload';
import { DataTable, Filters, FormModal, KV, Link2, Modal, Notice, Sel, StudentPicker, Tabs, arr, classLabel, errMsg, inputCls, useAction, useClasses, useGet, useTerms, studentName } from '@/features/shared/ops/kit';

const Del = ({ onClick }: { onClick: () => void }) => <button type="button" className="text-red-600 text-sm hover:underline" onClick={(e) => { e.stopPropagation(); onClick(); }}>Delete</button>;
const onErr = (e: unknown, set: (m: string) => void) => set(errMsg(e));

/* ---------- grading scales ---------- */
interface Band { grade: string; min_score: string; max_score: string; remark: string; gpa_point: string }
const blankBand = (): Band => ({ grade: '', min_score: '', max_score: '', remark: '', gpa_point: '' });

function ScaleEditor({ row, onClose, onDone }: { row?: any; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(row?.name ?? '');
  const [isDefault, setIsDefault] = useState(!!row?.is_default);
  const [bands, setBands] = useState<Band[]>(() => (arr(row?.items).length ? arr(row.items).map((i: any) => ({ grade: i.grade, min_score: String(i.min_score), max_score: String(i.max_score), remark: i.remark ?? '', gpa_point: i.gpa_point != null ? String(i.gpa_point) : '' })) : [blankBand()]));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const setB = (i: number, k: keyof Band, v: string) => setBands((b) => b.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  async function save() {
    setErr('');
    if (!name.trim()) return setErr('Give the scale a name.');
    if (bands.some((b) => !b.grade.trim() || b.min_score === '' || b.max_score === '')) return setErr('Every band needs a grade, a minimum and a maximum score.');
    const items = bands.map((b) => ({ grade: b.grade.trim(), min_score: Number(b.min_score), max_score: Number(b.max_score), remark: b.remark.trim() || null, gpa_point: b.gpa_point === '' ? null : Number(b.gpa_point) }));
    setBusy(true);
    try {
      await call(row ? 'put' : 'post', row ? `/school/grading-scales/${row.id}` : '/school/grading-scales', { body: { name: name.trim(), is_default: isDefault, items } });
      onDone();
      onClose();
    } catch (e) {
      onErr(e, setErr);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={row ? 'Edit grading scale' : 'New grading scale'} onClose={onClose} wide>
      {err && <Notice kind="error">{err}</Notice>}
      <div className="space-y-3 text-sm">
        <label className="block">Name<input className={inputCls} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Default scale for new results</label>
        <table className="w-full">
          <thead><tr className="text-left text-slate-500"><th className="pr-2">Grade</th><th className="pr-2">From</th><th className="pr-2">To</th><th className="pr-2">Remark</th><th className="pr-2">GPA</th><th /></tr></thead>
          <tbody>
            {bands.map((b, i) => (
              <tr key={i}>
                <td className="pr-2 py-1"><input className={inputCls} maxLength={6} value={b.grade} onChange={(e) => setB(i, 'grade', e.target.value)} /></td>
                <td className="pr-2 py-1"><input className={inputCls} type="number" min={0} max={100} value={b.min_score} onChange={(e) => setB(i, 'min_score', e.target.value)} /></td>
                <td className="pr-2 py-1"><input className={inputCls} type="number" min={0} max={100} value={b.max_score} onChange={(e) => setB(i, 'max_score', e.target.value)} /></td>
                <td className="pr-2 py-1"><input className={inputCls} maxLength={40} value={b.remark} onChange={(e) => setB(i, 'remark', e.target.value)} /></td>
                <td className="pr-2 py-1"><input className={inputCls} type="number" step="any" min={0} max={10} value={b.gpa_point} onChange={(e) => setB(i, 'gpa_point', e.target.value)} /></td>
                <td className="py-1">{bands.length > 1 && <Del onClick={() => setBands((x) => x.filter((_, j) => j !== i))} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {bands.length < 20 && <Button variant="ghost" onClick={() => setBands((x) => [...x, blankBand()])}>Add band</Button>}
        <p className="text-xs text-slate-500">Bands must cover 0 to 100 without overlapping. Published results keep the grades they were published with.</p>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save scale'}</Button></div>
      </div>
    </Modal>
  );
}

function GradingScales() {
  const qc = useQueryClient();
  const q = useGet<any>('grading-scales', '/school/grading-scales');
  const [edit, setEdit] = useState<null | { row?: any }>(null);
  return (
    <Card title="Grading scales" action={<Button onClick={() => setEdit({})}>New scale</Button>}>
      <DataTable rows={arr(q.data)} loading={q.isLoading} error={q.error} empty="No grading scales."
        cols={[
          { head: 'Name', cell: (r: any) => <span>{r.name}{r.is_default && <span className="ml-2 text-xs text-brand-600">default</span>}</span> },
          { head: 'Bands', cell: (r: any) => arr(r.items).map((i: any) => `${i.grade} ${i.min_score}-${i.max_score}`).join(' · ') },
          { head: '', cell: (r: any) => <Link2 onClick={() => setEdit({ row: r })}>Edit</Link2> },
        ]} />
      {edit && <ScaleEditor row={edit.row} onClose={() => setEdit(null)} onDone={() => qc.invalidateQueries({ queryKey: ['grading-scales'] })} />}
    </Card>
  );
}

/* ---------- assessment components (score columns) ---------- */
const KINDS = ['ca', 'test', 'exam', 'project', 'other'].map((k) => ({ value: k, label: k === 'ca' ? 'Continuous assessment' : k[0].toUpperCase() + k.slice(1) }));
function Components() {
  const qc = useQueryClient();
  const q = useGet<any>('assessment-components', '/school/assessment-components');
  const levels = useGet<any>('levelpick', '/school/class-levels');
  const [edit, setEdit] = useState<null | { row?: any }>(null);
  const levelOpts = arr(levels.data).map((l: any) => ({ value: l.id, label: l.name }));
  const rows = arr(q.data?.components);
  return (
    <Card title="Score columns" action={<Button onClick={() => setEdit({})}>Add column</Button>}>
      {q.data && <p className="text-sm text-slate-500 mb-2">All-classes columns add up to <b>{q.data.total_max}</b> of 100. Classes with their own columns (set on Grading &amp; Weights) use only those.</p>}
      <DataTable rows={rows} loading={q.isLoading} error={q.error} empty="No score columns yet."
        cols={[
          { head: '#', cell: (r: any) => r.position ?? '' },
          { head: 'Name', cell: (r: any) => r.name },
          { head: 'Code', cell: (r: any) => r.code },
          { head: 'Max', right: true, cell: (r: any) => r.max_score },
          { head: 'Kind', cell: (r: any) => r.kind },
          { head: 'Applies to', cell: (r: any) => ((r.classes ?? []).length ? r.classes.join(', ') : r.class_level_id ? levelOpts.find((l) => l.value === r.class_level_id)?.label ?? `Level #${r.class_level_id}` : 'All classes') },
          { head: '', cell: (r: any) => <Link2 onClick={() => setEdit({ row: r })}>Edit</Link2> },
        ]} />
      {edit && (
        <FormModal title={edit.row ? 'Edit score column' : 'Add score column'} initial={edit.row} onClose={() => setEdit(null)}
          fields={[
            { name: 'name', label: 'Name', required: true, maxLength: 60, help: 'e.g. First test' },
            { name: 'code', label: 'Code', required: true, maxLength: 12, pattern: /^[A-Za-z0-9_-]+$/, patternMsg: 'Letters, numbers, - and _ only' },
            { name: 'max_score', label: 'Maximum score', type: 'number', required: true, min: 1, max: 100 },
            { name: 'position', label: 'Order', type: 'number', min: 1 },
            { name: 'kind', label: 'Kind', type: 'select', options: KINDS },
            ...(edit.row ? [] : [{ name: 'class_level_id', label: 'Only for class level (leave blank for all)', type: 'select' as const, options: levelOpts }]),
          ]}
          onSubmit={async (v) => {
            const body = { ...v, class_level_id: v.class_level_id ? Number(v.class_level_id) : undefined };
            await call(edit.row ? 'put' : 'post', edit.row ? `/school/assessment-components/${edit.row.id}` : '/school/assessment-components', { body });
            qc.invalidateQueries({ queryKey: ['assessment-components'] });
          }} />
      )}
    </Card>
  );
}

/* ---------- templates ---------- */
const FIELD_HINTS = ['total', 'average', 'grade', 'position', 'remark', 'teacher_comment', 'principal_comment'];
function TemplateEditor({ row, onClose, onDone }: { row?: any; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(row?.name ?? '');
  const [type, setType] = useState(row?.type ?? 'termly');
  const [cols, setCols] = useState<{ label: string; field: string }[]>(() => (arr(row?.columns).length ? arr(row.columns).map((c: any) => (typeof c === 'string' ? { label: c, field: c } : { label: c.label ?? c.name ?? '', field: c.field ?? c.name ?? '' })) : [{ label: 'Total', field: 'total' }]));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setErr('');
    const columns = cols.filter((c) => c.label.trim() && c.field.trim()).map((c) => ({ label: c.label.trim(), field: c.field.trim() }));
    if (!name.trim()) return setErr('Name the template.');
    if (!columns.length) return setErr('Add at least one column.');
    setBusy(true);
    try {
      await call(row ? 'put' : 'post', row ? `/school/result-templates/${row.id}` : '/school/result-templates', { body: { name: name.trim(), type, columns } });
      onDone();
      onClose();
    } catch (e) {
      onErr(e, setErr);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={row ? 'Edit template' : 'New result template'} onClose={onClose} wide>
      {err && <Notice kind="error">{err}</Notice>}
      <div className="space-y-3 text-sm">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">Name<input className={inputCls} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="block">Type<select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}><option value="termly">Termly</option><option value="annual">Annual</option><option value="combined">Combined</option></select></label>
        </div>
        <div>
          <div className="font-medium mb-1">Columns</div>
          {cols.map((c, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={inputCls} placeholder="Heading" value={c.label} onChange={(e) => setCols((x) => x.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))} />
              <input className={inputCls} placeholder="Field" list="result-fields" value={c.field} onChange={(e) => setCols((x) => x.map((y, j) => (j === i ? { ...y, field: e.target.value } : y)))} />
              {cols.length > 1 && <Del onClick={() => setCols((x) => x.filter((_, j) => j !== i))} />}
            </div>
          ))}
          <datalist id="result-fields">{FIELD_HINTS.map((f) => <option key={f} value={f} />)}</datalist>
          <Button variant="ghost" onClick={() => setCols((x) => [...x, { label: '', field: '' }])}>Add column</Button>
        </div>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save template'}</Button></div>
      </div>
    </Modal>
  );
}

function Templates() {
  const qc = useQueryClient();
  const q = useGet<any>('result-templates', '/school/result-templates');
  const a = useAction(() => qc.invalidateQueries({ queryKey: ['result-templates'] }));
  const [edit, setEdit] = useState<null | { row?: any }>(null);
  return (
    <Card title="Report templates" action={<Button onClick={() => setEdit({})}>New template</Button>}>
      {a.banner}
      <DataTable rows={arr(q.data)} loading={q.isLoading} error={q.error} empty="No templates yet. A template is needed to export report cards."
        cols={[
          { head: 'Name', cell: (r: any) => r.name },
          { head: 'Type', cell: (r: any) => r.type },
          { head: 'Columns', cell: (r: any) => arr(r.columns).map((c: any) => (typeof c === 'string' ? c : c.label ?? c.name)).join(', ') },
          { head: '', cell: (r: any) => <span className="flex gap-3 justify-end"><Link2 onClick={() => setEdit({ row: r })}>Edit</Link2><Del onClick={() => a.run(() => call('delete', `/school/result-templates/${r.id}`), { confirm: 'Delete this template?' })} /></span> },
        ]} />
      {edit && <TemplateEditor row={edit.row} onClose={() => setEdit(null)} onDone={() => qc.invalidateQueries({ queryKey: ['result-templates'] })} />}
    </Card>
  );
}

/* ---------- signatories (signature image uploaded to media/upload) ---------- */
function SignatoryForm({ row, onClose, onDone }: { row?: any; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(row?.name ?? '');
  const [role, setRole] = useState(row?.role ?? '');
  const [path, setPath] = useState<string | null>(row?.signature_path ?? null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setErr('');
    if (!name.trim() || !role.trim()) return setErr('Name and role are required.');
    setBusy(true);
    try {
      await call(row ? 'put' : 'post', row ? `/school/result-signatories/${row.id}` : '/school/result-signatories', { body: { name: name.trim(), role: role.trim(), signature_path: path } });
      onDone();
      onClose();
    } catch (e) {
      onErr(e, setErr);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={row ? 'Edit signatory' : 'Add signatory'} onClose={onClose}>
      {err && <Notice kind="error">{err}</Notice>}
      <div className="space-y-3 text-sm">
        <label className="block">Name<input className={inputCls} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="block">Role<input className={inputCls} maxLength={80} placeholder="e.g. Principal" value={role} onChange={(e) => setRole(e.target.value)} /></label>
        <FileUpload label="Signature image" accept="image/png,image/jpeg,image/webp" value={path} related={{ type: 'result_signatory', id: row?.id }} onUploaded={(m) => setPath(m.path)} onClear={() => setPath(null)} hint="PNG with a transparent background works best." />
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button></div>
      </div>
    </Modal>
  );
}

function Signatories() {
  const qc = useQueryClient();
  const q = useGet<any>('result-signatories', '/school/result-signatories');
  const a = useAction(() => qc.invalidateQueries({ queryKey: ['result-signatories'] }));
  const [edit, setEdit] = useState<null | { row?: any }>(null);
  return (
    <Card title="Signatories" action={<Button onClick={() => setEdit({})}>Add signatory</Button>}>
      {a.banner}
      <DataTable rows={arr(q.data)} loading={q.isLoading} error={q.error} empty="No signatories yet."
        cols={[
          { head: 'Name', cell: (r: any) => r.name },
          { head: 'Role', cell: (r: any) => r.role },
          { head: 'Signature', cell: (r: any) => (r.signature_path ? 'Uploaded' : '—') },
          { head: '', cell: (r: any) => <span className="flex gap-3 justify-end"><Link2 onClick={() => setEdit({ row: r })}>Edit</Link2><Del onClick={() => a.run(() => call('delete', `/school/result-signatories/${r.id}`), { confirm: 'Delete this signatory?' })} /></span> },
        ]} />
      {edit && <SignatoryForm row={edit.row} onClose={() => setEdit(null)} onDone={() => qc.invalidateQueries({ queryKey: ['result-signatories'] })} />}
    </Card>
  );
}

/* ---------- composite subjects ---------- */
function CompositeForm({ row, subjects, onClose, onDone }: { row?: any; subjects: { value: number; label: string }[]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(row?.name ?? '');
  const [subjectId, setSubjectId] = useState(row?.subject_id ? String(row.subject_id) : '');
  const [parts, setParts] = useState<{ subject_id: string; weight: string }[]>(() => (arr(row?.components).length ? arr(row.components).map((c: any) => ({ subject_id: String(c.subject_id ?? c.component_subject_id), weight: String(c.weight) })) : [{ subject_id: '', weight: '' }, { subject_id: '', weight: '' }]));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setErr('');
    const components = parts.filter((p) => p.subject_id).map((p) => ({ subject_id: Number(p.subject_id), weight: Number(p.weight || 0) }));
    if (!name.trim()) return setErr('Name is required.');
    if (!components.length) return setErr('Add at least one component subject.');
    setBusy(true);
    try {
      await call(row ? 'put' : 'post', row ? `/school/composite-subjects/${row.id}` : '/school/composite-subjects', { body: { name: name.trim(), subject_id: subjectId ? Number(subjectId) : null, components } });
      onDone();
      onClose();
    } catch (e) {
      onErr(e, setErr);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={row ? 'Edit composite subject' : 'New composite subject'} onClose={onClose} wide>
      {err && <Notice kind="error">{err}</Notice>}
      <div className="space-y-3 text-sm">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">Name<input className={inputCls} maxLength={120} placeholder="e.g. Basic Science & Technology" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="block">Reported as subject (optional)<select className={inputCls} value={subjectId} onChange={(e) => setSubjectId(e.target.value)}><option value="">—</option>{subjects.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></label>
        </div>
        <div className="font-medium">Component subjects and weights (%)</div>
        {parts.map((p, i) => (
          <div key={i} className="flex gap-2">
            <select className={inputCls} value={p.subject_id} onChange={(e) => setParts((x) => x.map((y, j) => (j === i ? { ...y, subject_id: e.target.value } : y)))}><option value="">Choose subject</option>{subjects.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select>
            <input className={`${inputCls} w-28`} type="number" min={0} max={100} placeholder="Weight" value={p.weight} onChange={(e) => setParts((x) => x.map((y, j) => (j === i ? { ...y, weight: e.target.value } : y)))} />
            {parts.length > 1 && <Del onClick={() => setParts((x) => x.filter((_, j) => j !== i))} />}
          </div>
        ))}
        <Button variant="ghost" onClick={() => setParts((x) => [...x, { subject_id: '', weight: '' }])}>Add component</Button>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button></div>
      </div>
    </Modal>
  );
}

function Composites() {
  const qc = useQueryClient();
  const q = useGet<any>('composite-subjects', '/school/composite-subjects');
  const subj = useGet<any>('subjectpick', '/school/subjects');
  const subjects = arr(subj.data).map((s: any) => ({ value: s.id, label: s.name }));
  const name = (id: number) => subjects.find((s) => s.value === id)?.label ?? `#${id}`;
  const a = useAction(() => qc.invalidateQueries({ queryKey: ['composite-subjects'] }));
  const [edit, setEdit] = useState<null | { row?: any }>(null);
  return (
    <Card title="Composite subjects" action={<Button onClick={() => setEdit({})}>New composite</Button>}>
      {a.banner}
      <DataTable rows={arr(q.data)} loading={q.isLoading} error={q.error} empty="No composite subjects."
        cols={[
          { head: 'Name', cell: (r: any) => r.name },
          { head: 'Made up of', cell: (r: any) => arr(r.components).map((c: any) => `${name(c.subject_id)} (${c.weight}%)`).join(', ') },
          { head: '', cell: (r: any) => <span className="flex gap-3 justify-end"><Link2 onClick={() => setEdit({ row: r })}>Edit</Link2><Del onClick={() => a.run(() => call('delete', `/school/composite-subjects/${r.id}`), { confirm: 'Delete this composite subject?' })} /></span> },
        ]} />
      {edit && <CompositeForm row={edit.row} subjects={subjects} onClose={() => setEdit(null)} onDone={() => qc.invalidateQueries({ queryKey: ['composite-subjects'] })} />}
    </Card>
  );
}

/* ---------- subject registrations ---------- */
function Registrations() {
  const qc = useQueryClient();
  const t = useTerms();
  const classes = useClasses();
  const subj = useGet<any>('subjectpick', '/school/subjects');
  const [termId, setTermId] = useState('');
  const [classId, setClassId] = useState('');
  const termSel = termId || String((t.terms.find((x: any) => x.is_current) ?? t.terms[0])?.id ?? '');
  const q = useGet<any>('subject-registrations', '/school/subject-registrations', { term_id: termSel, class_id: classId }, !!termSel);
  const [student, setStudent] = useState<any>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [open, setOpen] = useState(false);
  const a = useAction(() => qc.invalidateQueries({ queryKey: ['subject-registrations'] }));
  const rows = arr(q.data);
  const byStudent = rows.reduce((m: Record<string, any>, r: any) => { (m[r.student_id] ??= { student_id: r.student_id, student: r.student, subjects: [] as any[] }).subjects.push(r); return m; }, {});
  const grouped = Object.values(byStudent) as any[];
  return (
    <Card title="Subject registrations" action={<Button disabled={!termSel} onClick={() => { setStudent(null); setPicked([]); setOpen(true); }}>Register a student</Button>}>
      <Filters>
        <Sel value={termSel} onChange={setTermId} placeholder="Choose term" options={t.terms.map((x: any) => ({ value: x.id, label: x.label }))} />
        <Sel value={classId} onChange={setClassId} placeholder="All classes" options={arr(classes.data).map((c: any) => ({ value: c.id, label: classLabel(c) }))} />
      </Filters>
      {a.banner}
      <DataTable rows={grouped} loading={q.isLoading} error={q.error} empty="No subject registrations for this term. Students without registrations take every subject of their class."
        cols={[
          { head: 'Student', cell: (r: any) => r.student },
          { head: 'Subjects', cell: (r: any) => r.subjects.map((s: any) => s.subject).join(', ') },
          { head: '', cell: (r: any) => <Link2 onClick={() => { setStudent({ id: r.student_id, name: r.student }); setPicked(r.subjects.map((s: any) => s.subject_id)); setOpen(true); }}>Change</Link2> },
        ]} />
      {open && (
        <Modal title="Register subjects" onClose={() => setOpen(false)} wide>
          <div className="space-y-3 text-sm">
            <StudentPicker value={student} onPick={setStudent} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
              {arr(subj.data).map((s: any) => (
                <label key={s.id} className="flex items-center gap-2"><input type="checkbox" checked={picked.includes(s.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id)))} />{s.name}</label>
              ))}
            </div>
            <p className="text-xs text-slate-500">This replaces the student's subjects for the selected term.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button disabled={!student || a.busy} onClick={async () => { await a.run(() => call('post', '/school/subject-registrations', { body: { term_id: Number(termSel), student_id: student.id, subject_ids: picked } })); setOpen(false); }}>Save</Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

/* ---------- transcripts & exports ---------- */
export function TranscriptsExports() {
  const t = useTerms();
  const classes = useClasses();
  const templates = useGet<any>('result-templates', '/school/result-templates');
  const [student, setStudent] = useState<any>(null);
  const [transcript, setTranscript] = useState<any>(null);
  const [tErr, setTErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState({ template_id: '', type: 'class', class_id: '', term_id: '' });
  const [expStudent, setExpStudent] = useState<any>(null);
  const [expOut, setExpOut] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  async function generate() {
    if (!student) return;
    setTErr('');
    setBusy(true);
    try {
      const r = await call('post', '/school/transcripts', { body: { student_id: student.id } });
      const full = await call('get', `/school/transcripts/${r.data.id}`);
      setTranscript(full.data);
    } catch (e) {
      onErr(e, setTErr);
    } finally {
      setBusy(false);
    }
  }
  async function runExport() {
    setExpOut(null);
    if (!exp.template_id) return setExpOut({ kind: 'error', text: 'Choose a template (create one under Report templates).' });
    if (exp.type === 'class' && !exp.class_id) return setExpOut({ kind: 'error', text: 'Choose a class.' });
    if (exp.type === 'student' && !expStudent) return setExpOut({ kind: 'error', text: 'Choose a student.' });
    const body: Record<string, unknown> = { template_id: Number(exp.template_id), type: exp.type };
    if (exp.term_id) body.term_id = Number(exp.term_id);
    if (exp.type === 'class') body.class_id = Number(exp.class_id);
    else body.student_id = expStudent.id;
    try {
      const r = await call('post', '/school/results/export', { body });
      setExpOut({ kind: 'ok', text: `${r.message ?? 'Export generated.'} Saved as ${r.data?.path}.` });
    } catch (e) {
      setExpOut({ kind: 'error', text: errMsg(e) });
    }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card title="Transcript">
        <div className="space-y-3">
          <StudentPicker value={student} onPick={(s) => { setStudent(s); setTranscript(null); }} />
          {tErr && <Notice kind="error">{tErr}</Notice>}
          <Button disabled={!student || busy} onClick={generate}>{busy ? 'Generating…' : 'Generate transcript'}</Button>
          {transcript && (
            <div className="text-sm">
              <KV k="Reference" v={transcript.reference} />
              <KV k="Status" v={transcript.status} />
              {transcript.url && <a href={transcript.url} target="_blank" rel="noreferrer" className="inline-flex mt-2 px-3 py-2 rounded-lg bg-[#0A2540] text-white text-xs font-semibold">Download transcript — uses each class's sheet design</a>}
              {arr(transcript.sessions).map((s: any, i: number) => (
                <div key={i} className="mt-3">
                  <div className="font-medium">{s.session ?? `Session ${i + 1}`}{s.annual ? ` · annual average ${s.annual.average}${s.annual.grade ? ` (${s.annual.grade})` : ''}` : ''}</div>
                  {arr(s.terms).map((tm: any, j: number) => <KV key={j} k={`${tm.term} · ${tm.class}`} v={`Average ${tm.summary?.average ?? '—'}${tm.summary?.position ? ` · position ${tm.summary.position}` : ''}${tm.summary?.grade ? ` · ${tm.summary.grade}` : ''}`} />)}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
      <Card title="Export report cards (PDF)">
        <div className="space-y-3 text-sm">
          {expOut && <Notice kind={expOut.kind}>{expOut.text}</Notice>}
          <label className="block">Template<select className={inputCls} value={exp.template_id} onChange={(e) => setExp({ ...exp, template_id: e.target.value })}><option value="">Choose template</option>{arr(templates.data).map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
          <label className="block">For<select className={inputCls} value={exp.type} onChange={(e) => setExp({ ...exp, type: e.target.value })}><option value="class">A whole class</option><option value="student">One student</option></select></label>
          {exp.type === 'class' ? (
            <label className="block">Class<select className={inputCls} value={exp.class_id} onChange={(e) => setExp({ ...exp, class_id: e.target.value })}><option value="">Choose class</option>{arr(classes.data).map((c: any) => <option key={c.id} value={c.id}>{classLabel(c)}</option>)}</select></label>
          ) : (
            <StudentPicker value={expStudent} onPick={setExpStudent} />
          )}
          <label className="block">Term<select className={inputCls} value={exp.term_id} onChange={(e) => setExp({ ...exp, term_id: e.target.value })}><option value="">Latest</option>{t.terms.map((x: any) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
          <Button onClick={runExport}>Generate PDF</Button>
          {expStudent && exp.type === 'student' && <p className="text-xs text-slate-500">{studentName(expStudent)}</p>}
        </div>
      </Card>
    </div>
  );
}

const TABS = [
  { id: 'scales', label: 'Grading' },
  { id: 'columns', label: 'Score columns' },
  { id: 'templates', label: 'Templates' },
  { id: 'signatories', label: 'Signatories' },
  { id: 'composites', label: 'Composite subjects' },
  { id: 'registrations', label: 'Subject registrations' },
  { id: 'transcripts', label: 'Transcripts & export' },
];

export function ResultsSetup() {
  const [tab, setTab] = useState('scales');
  return (
    <div>
      <Tabs value={tab} onChange={setTab} tabs={TABS} />
      {tab === 'scales' && <GradingScales />}
      {tab === 'columns' && <Components />}
      {tab === 'templates' && <Templates />}
      {tab === 'signatories' && <Signatories />}
      {tab === 'composites' && <Composites />}
      {tab === 'registrations' && <Registrations />}
      {tab === 'transcripts' && <TranscriptsExports />}
    </div>
  );
}
